/**
 * Per-platform sync-client capabilities.
 *
 * The sync client is shared by web AND native, but some behaviours are a
 * client feature only some platforms implement. A platform OPTS IN by calling
 * `setSyncClientCapabilities({...})` at sync init; every flag defaults to OFF,
 * so a platform that never calls it is wholly unaffected.
 *
 * Two kinds of flag live here — keep them apart:
 *   - WIRE capabilities reach the server: `coldDecks` (UserDo — `performSyncInit`
 *     copies it into the `sync-init` payload) and `lazySgCards` (StudyGroupDo —
 *     `buildSgSyncInit` sends it as `lazyCards: true` on the full manifest).
 *   - CLIENT-LOCAL capabilities change only how this client hydrates and
 *     loads; they must NEVER enter the payload (pinned in
 *     `user-do-adapter.spec.ts`): `lazyCardHydration`.
 *
 * Both clients opt into `coldDecks` today — web in `dexie-sync-storage.ts`,
 * native in `SyncProviderRN` — so the server-side cold filter is NOT what makes
 * the two platforms behave differently on a large account. This is the
 * mechanism that made "ship cold decks on web first" safe, and it is what
 * lets `lazyCardHydration` ship on native first.
 */

export type SyncClientCapabilities = {
  /** WIRE. Client understands cold decks: withholds-tolerant hydration, an
   *  archived UI, and an on-demand fetch. Server only filters when true. */
  coldDecks: boolean;
  /**
   * CLIENT-LOCAL. Hydrate reads deck metadata and per-deck study summaries
   * only; card bodies load per deck — a hot set right after first paint, the
   * rest when a deck is opened — from this device's storage first
   * (`internal/deck-disk-load.ts`). Off: every warm deck's cards are read and
   * decoded before `isHydrated` (the eager path).
   *
   * Independent of this flag — both modes, by design: the summary bridge's
   * disk recompute for a stale deck whose bodies are not resident; the
   * one-time summary backfill for decks without a trusted row (LOCAL rows,
   * one read per non-resident deck, once — marker `settings/deckSummaryBackfill`);
   * and the stage-1 force-load of decks with unsynced offline edits. Each
   * fixes a bug the eager path has too; none touches the wire.
   */
  lazyCardHydration: boolean;
  /**
   * WIRE (StudyGroupDo). A study group's shared cards arrive per deck, on
   * demand, instead of with the group: the `sg-sync-init` reply streams cards
   * only for decks this client already holds complete (the ones it sent a
   * `deckTimestamps` entry for); every other stale deck rides the header as
   * properties alone and its cards are fetched with `sg-request-deck-cards`
   * the first time something needs them — on mobile, only the mint of a
   * missing mirror (`study-groups/sg-deck-cards-load.ts`). Off: the reply
   * streams every stale deck, so the whole group is resident after sync (web,
   * whose group page counts from the shared cards).
   */
  lazySgCards: boolean;
};

const capabilities: SyncClientCapabilities = {
  coldDecks: false,
  lazyCardHydration: false,
  lazySgCards: false,
};

/** Merge platform capability flags (call once at sync-client init). */
export function setSyncClientCapabilities(next: Partial<SyncClientCapabilities>): void {
  Object.assign(capabilities, next);
}

/** Read the current capabilities (defaults: everything off). */
export function getSyncClientCapabilities(): Readonly<SyncClientCapabilities> {
  return capabilities;
}
